import { computed, onMounted, ref, Ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import router from "@/router";
import {
  NumbersMapStruct,
  PremiumCategoryStruct,
  PremiumCategoryTabStruct,
} from "@/struct/PremiumStruct";

export function usePremiumPhones() {
  const store = useStore();
  const { t, locale } = useI18n();
  const searchValue = ref<string>("");

  const activeNumber = ref<number>();
  const activeNumberPrice = ref<number>();
  const activeNumberType = ref<string>("");

  const goldenLimit = ref<number>(10);
  const platinumLimit = ref<number>(10);
  const silverLimit = ref<number>(10);
  const bronzeLimit = ref<number>(10);
  const regularLimit = ref<number>(10);

  const openModal = ref<boolean>(false);
  const openModalLowMoney = ref<boolean>(false);
  const openModalTwoParts = ref<boolean>(false);
  const openModalFirstPayment = ref<boolean>(false);
  const openModalSecondPayment = ref<boolean>(false);
  const errorModal = ref<boolean>(false);
  const errorSixMonthsModal = ref<boolean>(false);
  const successModal = ref<boolean>(false);
  const isLoadingChangeNumber = ref<boolean>(false);

  const configDataBrand = computed(
    () => store.getters["brand/configDataBrand"]
  );
  const walletAmount = computed(() => store.getters["tariff/walletAmount"]);
  const walletAmountCalc = computed(
    () => store.getters["tariff/walletAmountCalc"]
  );

  const isLoadingListing = computed(() => store.state.premium.isLoadingListing);
  const premiumNumbersMap = computed(() => store.state.premium.numbersMap);
  const tabs = computed(() => store.getters["premium/premiumTabs"]);

  const activeTab = ref<PremiumCategoryTabStruct>(tabs.value[0]);

  const updateDefaultTabTitle = () => {
    tabs.value[0].title = t("premium.tabs.all");
    if (activeTab.value.name === "all") {
      activeTab.value.title = t("premium.tabs.all");
    }
  };

  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, "");

    if (cleaned.length === 11 && cleaned.startsWith("7")) {
      const part1 = cleaned.substring(1, 4);
      const part2 = cleaned.substring(4, 7);
      const part3 = cleaned.substring(7, 9);
      const part4 = cleaned.substring(9, 11);
      return `+7 ${part1} ${part2} ${part3} ${part4}`;
    }

    return phone;
  };

  const initSearch = () => {
    const queryDigits =
      searchValue.value.trim().length === 11
        ? searchValue.value.trim().replace(/^8/, "7")
        : searchValue.value.trim();
    store.dispatch("premium/updatePremiumNumbers", {
      search: queryDigits,
    });
    return;
  };
  const loadMore = (categoryName: string) => {
    console.log("loadMore", categoryName);
    const numbersObj = premiumNumbersMap.value?.get(categoryName);
    store.dispatch("premium/fetchNumbersByCategory", {
      category: categoryName,
      search: searchValue.value.trim(),
      start: numbersObj.numbers.length,
    });
  };

  const visibleCategories = computed(() => {
    const activeName = activeTab.value?.name;

    if (!activeName || activeName === "all") {
      return store.state.premium.categories;
    }

    return store.state.premium.categories.filter(
      (category: PremiumCategoryStruct) => category.name === activeName
    );
  });
  const isAllCategoriesEmpty = computed(() => {
    const allCategoryData: NumbersMapStruct[] = Array.from(
      premiumNumbersMap.value.values()
    );
    if (!allCategoryData.length) return true;
    return allCategoryData.every(
      (item: NumbersMapStruct) => item.numbers.length === 0
    );
  });

  const getAllNumbers = () => {
    store.dispatch("premium/updatePremiumCategories").then((data) => {
      store.dispatch("premium/updatePremiumNumbers");
    });
  };

  const selectNumber = (
    number: number,
    price: string | number,
    type: string
  ) => {
    const roundedPrice = Math.round(Number(price));

    if (activeNumber.value === number) {
      if ((walletAmount.value as number) >= roundedPrice) {
        openModal.value = true;
      } else if (roundedPrice >= 15000) {
        openModalTwoParts.value = true;
      } else {
        openModalLowMoney.value = true;
      }
    } else {
      activeNumber.value = number;
      activeNumberPrice.value = roundedPrice;
      activeNumberType.value = type;
    }
  };

  const closeAllModals = () => {
    openModal.value = false;
    openModalLowMoney.value = false;
    openModalTwoParts.value = false;
    openModalFirstPayment.value = false;
    openModalSecondPayment.value = false;
    errorModal.value = false;
    errorSixMonthsModal.value = false;
    successModal.value = false;
  };

  const toPayment = () => {
    closeAllModals();
    store.dispatch("togglePaymentModal", true);
  };

  const closeSuccessModal = () => {
    router.push("/");
  };

  const changeNumberToPremium = (number: number, type: string) => {
    isLoadingChangeNumber.value = true;
    store
      .dispatch("premium/changeNumberToPremium", { number, type })
      .finally(() => {
        openModal.value = false;
        isLoadingChangeNumber.value = false;
      });
  };
  const tabsClasses = (name: string) => {
    return [activeTab.value.name === name ? "active" : ""];
  };

  const clickTab = (item: PremiumCategoryTabStruct) => {
    activeTab.value = item;
  };

  const showMoreCategory = (key: string) => {
    const limits: Record<string, Ref<number>> = {
      platinum: platinumLimit,
      golden: goldenLimit,
      silver: silverLimit,
      bronze: bronzeLimit,
      regular: regularLimit,
    };

    limits[key].value += 10;
  };

  watch(
    () => locale.value,
    () => {
      updateDefaultTabTitle();
    },
    { immediate: true }
  );

  watch(
    () => searchValue.value,
    () => {
      initSearch();
    }
  );

  onMounted(() => {
    getAllNumbers();
  });

  return {
    activeTab,
    activeNumber,
    activeNumberPrice,
    activeNumberType,
    closeAllModals,
    closeSuccessModal,
    errorModal,
    errorSixMonthsModal,
    formatPhone,
    initSearch,
    isLoadingChangeNumber,
    openModal,
    openModalFirstPayment,
    openModalLowMoney,
    openModalSecondPayment,
    openModalTwoParts,
    searchValue,
    selectNumber,
    showMoreCategory,
    successModal,
    t,
    tabs,
    tabsClasses,
    toPayment,
    walletAmount,
    walletAmountCalc,
    changeNumberToPremium,

    isLoadingListing,
    clickTab,
    loadMore,
    visibleCategories,
    isAllCategoriesEmpty,
    premiumNumbersMap,
    configDataBrand,
  };
}
